import type { GameState, Modifiers, TaleDef, TaleEntry } from './types';
import { BALANCE, FOOD_RESOURCES, JOBS, RESOURCES_BY_ID, talesForAge } from './content';
import { collectModifiers } from './modifiers';
import { computeProduction, type ProductionResult } from './production';
import { randomRange, weightedPick } from './random';
import { formatNumber } from './numbers';
import { assignedWorkers } from './state';

/** Longest single step the simulation will take in one call. */
export const MAX_TICK_SECONDS = 60;

export interface TickOutcome {
  modifiers: Modifiers;
  production: ProductionResult;
  talesFired: TaleEntry[];
}

/**
 * Advance the simulation in place by `deltaSeconds`.
 * Mutates `state` — callers that need immutability should clone first.
 */
export function advance(state: GameState, deltaSeconds: number): TickOutcome {
  const step = Math.min(Math.max(deltaSeconds, 0), MAX_TICK_SECONDS);
  const modifiers = collectModifiers(state);
  const production = computeProduction(state, modifiers, step);
  const talesFired: TaleEntry[] = [];

  if (step <= 0) return { modifiers, production, talesFired };

  applyResourceChanges(state, production, step);
  applyPopulation(state, modifiers, production, step);
  reconcileAssignments(state);
  decayBuffs(state, step);
  decayVigor(state, modifiers, step);
  rollTales(state, production, modifiers, step, talesFired);

  state.stats.totalPlaySeconds += step;
  state.peakPopulation = Math.max(state.peakPopulation, state.population);
  state.stats.bestPopulation = Math.max(state.stats.bestPopulation, state.population);

  return { modifiers, production, talesFired };
}

function applyResourceChanges(
  state: GameState,
  production: ProductionResult,
  step: number,
): void {
  for (const [resourceId, netRate] of Object.entries(production.net)) {
    const cap = production.caps[resourceId] ?? Infinity;
    const current = state.resources[resourceId] ?? 0;
    const updated = current + netRate * step;
    state.resources[resourceId] = Math.min(cap, Math.max(0, updated));

    const grossRate = production.production[resourceId] ?? 0;
    if (grossRate > 0) {
      state.stats.lifetimeProduced[resourceId] =
        (state.stats.lifetimeProduced[resourceId] ?? 0) + grossRate * step;
    }
  }
}

function applyPopulation(
  state: GameState,
  modifiers: Modifiers,
  production: ProductionResult,
  step: number,
): void {
  const demand = production.nutritionDemand;
  const supplied = production.nutritionSupplied;
  const isStarving = supplied < demand - 1e-9;

  if (isStarving) {
    const shortfall = demand <= 0 ? 0 : (demand - supplied) / demand;
    const loss = state.population * BALANCE.starvationRate * shortfall * step;
    state.population = Math.max(1, state.population - loss);
    return;
  }

  const room = production.shelter - state.population;
  if (room <= 0) return;

  // Growth scales with how much food is being banked beyond what is eaten.
  let surplusNutrition = 0;
  for (const foodResource of FOOD_RESOURCES) {
    const net = production.net[foodResource.id] ?? 0;
    if (net > 0) surplusNutrition += net * (foodResource.nutrition ?? 0) * modifiers.nutrition;
  }
  const surplusFactor = demand <= 0 ? 1 : Math.min(1, surplusNutrition / demand);
  if (surplusFactor <= 0) return;

  state.population += room * BALANCE.baseGrowthRate * modifiers.growth * surplusFactor * step;
  state.population = Math.min(state.population, production.shelter);
}

/** Release workers when the population has shrunk below the number assigned. */
function reconcileAssignments(state: GameState): void {
  const capacity = Math.floor(state.population);
  let assigned = assignedWorkers(state);
  if (assigned <= capacity) return;

  // Take from the largest job first so no single role is wiped out.
  while (assigned > capacity) {
    let largestJobId: string | null = null;
    let largestCount = 0;
    for (const job of JOBS) {
      const count = state.jobs[job.id] ?? 0;
      if (count > largestCount) {
        largestCount = count;
        largestJobId = job.id;
      }
    }
    if (!largestJobId || largestCount <= 0) break;
    state.jobs[largestJobId] = largestCount - 1;
    assigned -= 1;
  }
}

function decayBuffs(state: GameState, step: number): void {
  if (state.buffs.length === 0) return;
  state.buffs = state.buffs
    .map((buff) => ({ ...buff, remainingSeconds: buff.remainingSeconds - step }))
    .filter((buff) => buff.remainingSeconds > 0);
}

function decayVigor(state: GameState, modifiers: Modifiers, step: number): void {
  if (state.vigorStacks <= 0) {
    state.vigorDecay = 0;
    return;
  }
  state.vigorStacks = Math.min(state.vigorStacks, Math.floor(modifiers.vigorMaxStacks));
  state.vigorDecay -= step;
  while (state.vigorDecay <= 0 && state.vigorStacks > 0) {
    state.vigorStacks -= 1;
    state.vigorDecay += BALANCE.vigorDecaySeconds;
  }
  if (state.vigorStacks <= 0) state.vigorDecay = 0;
}

function rollTales(
  state: GameState,
  production: ProductionResult,
  modifiers: Modifiers,
  step: number,
  fired: TaleEntry[],
): void {
  state.taleTimer -= step;
  // Guard against a single very long offline step firing hundreds of tales.
  let safety = 8;
  while (state.taleTimer <= 0 && safety-- > 0) {
    const candidates = talesForAge(state.ageIndex).filter(
      (tale) => (tale.minPopulation ?? 0) <= state.population,
    );
    const picked = weightedPick(state.seed, candidates, (tale) =>
      tale.favourable ? tale.weight * (1 + modifiers.taleLuck) : tale.weight,
    );
    state.seed = picked.seed;

    if (picked.item) {
      const entry = applyTale(state, picked.item, production);
      state.tales.unshift(entry);
      if (state.tales.length > BALANCE.taleLogLimit) state.tales.length = BALANCE.taleLogLimit;
      state.stats.totalTalesSeen += 1;
      fired.push(entry);
    }

    const interval = randomRange(
      state.seed,
      BALANCE.taleIntervalMinSeconds,
      BALANCE.taleIntervalMaxSeconds,
    );
    state.seed = interval.seed;
    state.taleTimer += interval.value;
  }
  if (state.taleTimer <= 0) state.taleTimer = BALANCE.taleIntervalMinSeconds;
}

function applyTale(
  state: GameState,
  tale: TaleDef,
  production: ProductionResult,
): TaleEntry {
  const summaryParts: string[] = [];
  // Floor keeps early-game and freshly-reset tales from granting nothing.
  const grantFloor = 6 * Math.pow(2.5, state.ageIndex);

  for (const effect of tale.effects) {
    switch (effect.kind) {
      case 'grantSeconds': {
        const rate = production.production[effect.resource] ?? 0;
        const amount = Math.max(rate * effect.seconds, grantFloor);
        const cap = production.caps[effect.resource] ?? Infinity;
        state.resources[effect.resource] = Math.min(
          cap,
          (state.resources[effect.resource] ?? 0) + amount,
        );
        summaryParts.push(
          `+${formatNumber(amount)} ${RESOURCES_BY_ID[effect.resource]?.name ?? effect.resource}`,
        );
        break;
      }
      case 'grantPerHead': {
        const amount = effect.amount * Math.max(1, state.population);
        const cap = production.caps[effect.resource] ?? Infinity;
        state.resources[effect.resource] = Math.min(
          cap,
          (state.resources[effect.resource] ?? 0) + amount,
        );
        summaryParts.push(
          `+${formatNumber(amount)} ${RESOURCES_BY_ID[effect.resource]?.name ?? effect.resource}`,
        );
        break;
      }
      case 'population': {
        const before = state.population;
        const target = state.population + effect.amount;
        state.population =
          effect.amount > 0 ? Math.min(production.shelter, target) : Math.max(1, target);
        const change = state.population - before;
        if (Math.abs(change) >= 0.5) {
          summaryParts.push(`${change > 0 ? '+' : ''}${Math.round(change)} tribe`);
        }
        break;
      }
      case 'buff': {
        state.buffs.push({
          id: `${tale.id}-${Math.round(state.stats.totalPlaySeconds)}`,
          amount: effect.amount,
          remainingSeconds: effect.durationSeconds,
        });
        summaryParts.push(
          `${effect.amount > 0 ? '+' : ''}${Math.round(effect.amount * 100)}% for ${Math.round(
            effect.durationSeconds,
          )}s`,
        );
        break;
      }
      case 'vigor': {
        state.vigorStacks += effect.stacks;
        state.vigorDecay = Math.max(state.vigorDecay, BALANCE.vigorDecaySeconds);
        summaryParts.push(`+${effect.stacks} Vigor`);
        break;
      }
    }
  }

  return {
    id: tale.id,
    title: tale.title,
    text: tale.text,
    at: state.stats.totalPlaySeconds,
    generation: state.generation,
    favourable: tale.favourable,
    summary: summaryParts.join(' · '),
  };
}
